/**
 * SceneTargetsEditor - Two-pane device targets editor for scenes
 *
 * Uses a master/detail pattern:
 * - Left pane: Compact list of device targets
 * - Right pane: Property picker and value editor for selected device
 */

import {
  type DevicesDevice,
  type DevicesDeviceState,
  type ScenesSceneDeviceRequest,
  useListDevices,
} from '@smarthome/api';
import {
  AlertCircle,
  Blinds,
  Fan,
  Lightbulb,
  LogOut,
  MoreVertical,
  Plus,
  Power,
  Search,
  Speaker,
  Trash2,
  Tv,
  X,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  generateStateSummary,
  getDefaultStateForDevice,
  getIncludedPropertyPaths,
  getPropertyDefsForDevice,
  getPropertyValue,
  hasIncludedProperties,
  isPropertyIncluded,
  type PropertyDef,
  pruneState,
  removeProperty,
  setPropertyValue,
} from '../targets';

import {
  Badge,
  Button,
  Dropdown,
  IconButton,
  Modal,
  Popover,
  Select,
  Slider,
  Toggle,
} from '@/components/ui';
import { cn } from '@/components/ui/utils';

interface SceneTargetsEditorProps {
  value: ScenesSceneDeviceRequest[];
  onChange: (targets: ScenesSceneDeviceRequest[]) => void;
  error?: string;
}

const DEVICE_ICONS: Record<string, React.ElementType> = {
  light: Lightbulb,
  switch: Power,
  fan: Fan,
  tv: Tv,
  speaker: Speaker,
  window_covering: Blinds,
};

// Color presets for light color property
const COLOR_PRESETS = [
  { name: 'Warm White', red: 255, green: 244, blue: 229 },
  { name: 'Cool White', red: 255, green: 255, blue: 255 },
  { name: 'Red', red: 255, green: 0, blue: 0 },
  { name: 'Orange', red: 255, green: 165, blue: 0 },
  { name: 'Yellow', red: 255, green: 255, blue: 0 },
  { name: 'Green', red: 0, green: 255, blue: 0 },
  { name: 'Blue', red: 0, green: 0, blue: 255 },
  { name: 'Purple', red: 128, green: 0, blue: 128 },
  { name: 'Pink', red: 255, green: 192, blue: 203 },
];

type DeviceLookup = Map<string, DevicesDevice>;

function getTargetSortLabel(target: ScenesSceneDeviceRequest, devicesByHumanId: DeviceLookup) {
  const device = target.id ? devicesByHumanId.get(target.id) : undefined;
  return (device?.name ?? device?.humanId ?? target.id ?? '').trim().toLowerCase();
}

function sortTargets(targets: ScenesSceneDeviceRequest[], devicesByHumanId: DeviceLookup) {
  return [...targets]
    .sort((a, b) => {
      const aLabel = getTargetSortLabel(a, devicesByHumanId);
      const bLabel = getTargetSortLabel(b, devicesByHumanId);
      if (aLabel === bLabel) {
        return (a.id ?? '').localeCompare(b.id ?? '');
      }
      return aLabel.localeCompare(bLabel);
    })
    .map((target, index) => ({ ...target, orderIndex: index }));
}

function getDeviceSortLabel(device: DevicesDevice) {
  return (device.name ?? device.humanId ?? device.id ?? '').trim().toLowerCase();
}

function sortDevices(devices: DevicesDevice[]) {
  return [...devices].sort((a, b) => {
    const aLabel = getDeviceSortLabel(a);
    const bLabel = getDeviceSortLabel(b);
    if (aLabel === bLabel) {
      return (a.humanId ?? a.id ?? '').localeCompare(b.humanId ?? b.id ?? '');
    }
    return aLabel.localeCompare(bLabel);
  });
}

export function SceneTargetsEditor({ value, onChange, error }: SceneTargetsEditorProps) {
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Auto-focus search input when add device modal opens
  useEffect(() => {
    if (showAddDeviceModal) {
      // Small delay to ensure modal is rendered
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [showAddDeviceModal]);

  const { data: devicesData, isLoading: devicesLoading } = useListDevices();
  const availableDevices = useMemo(() => devicesData?.devices ?? [], [devicesData?.devices]);
  const devicesByHumanId = useMemo(() => {
    return new Map(
      availableDevices.filter((device) => device.humanId).map((device) => [device.humanId!, device])
    );
  }, [availableDevices]);

  const sortedTargets = useMemo(
    () => sortTargets(value, devicesByHumanId),
    [value, devicesByHumanId]
  );

  useEffect(() => {
    if (sortedTargets.length === 0) {
      setSelectedTargetId(null);
      return;
    }
    setSelectedTargetId((prev) => {
      if (prev && sortedTargets.some((target) => target.id === prev)) {
        return prev;
      }
      return sortedTargets[0]?.id ?? null;
    });
  }, [sortedTargets]);

  // Devices not already in targets (targets use humanId as identifier)
  const selectableDevices = useMemo(() => {
    const targetHumanIds = new Set(value.map((t) => t.id));
    return availableDevices.filter((d) => d.humanId && !targetHumanIds.has(d.humanId));
  }, [availableDevices, value]);

  // Filtered devices for search
  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return selectableDevices;
    const query = searchQuery.toLowerCase();
    return selectableDevices.filter(
      (d) =>
        d.name?.toLowerCase().includes(query) ||
        d.humanId?.toLowerCase().includes(query) ||
        d.provider?.toLowerCase().includes(query) ||
        d.type?.toLowerCase().includes(query)
    );
  }, [selectableDevices, searchQuery]);

  const sortedFilteredDevices = useMemo(() => sortDevices(filteredDevices), [filteredDevices]);

  // Look up device by humanId (which is what targets store in the id field)
  const getDeviceInfo = useCallback(
    (humanId: string) => availableDevices.find((d) => d.humanId === humanId),
    [availableDevices]
  );

  const selectedTarget = useMemo(() => {
    if (!selectedTargetId) return null;
    return sortedTargets.find((target) => target.id === selectedTargetId) ?? null;
  }, [selectedTargetId, sortedTargets]);
  const selectedDevice = selectedTarget ? getDeviceInfo(selectedTarget.id) : null;

  // Handlers
  const handleAddDevice = useCallback(
    (deviceId: string) => {
      const device = availableDevices.find((d) => d.id === deviceId);
      if (!device || !device.humanId) return;

      // Create default state with only primary property
      const state = getDefaultStateForDevice(device);

      // Note: The API expects humanId in the id field, not the UUID
      const newTarget: ScenesSceneDeviceRequest = {
        id: device.humanId,
        state,
        orderIndex: value.length,
      };

      const newTargets = sortTargets([...value, newTarget], devicesByHumanId);
      onChange(newTargets);
      setSelectedTargetId(device.humanId);
      setShowAddDeviceModal(false);
      setSearchQuery('');
    },
    [availableDevices, devicesByHumanId, value, onChange]
  );

  const handleRemoveDevice = useCallback(
    (targetId: string) => {
      const removedIndex = sortedTargets.findIndex((target) => target.id === targetId);
      const newTargets = sortTargets(
        value.filter((target) => target.id !== targetId),
        devicesByHumanId
      );
      onChange(newTargets);

      setSelectedTargetId((prev) => {
        if (!prev) return newTargets[0]?.id ?? null;
        if (prev !== targetId) {
          return newTargets.some((target) => target.id === prev)
            ? prev
            : (newTargets[0]?.id ?? null);
        }
        if (newTargets.length === 0) return null;
        const nextIndex = Math.min(Math.max(removedIndex, 0), newTargets.length - 1);
        return newTargets[nextIndex]?.id ?? newTargets[0]?.id ?? null;
      });
    },
    [devicesByHumanId, onChange, sortedTargets, value]
  );

  const handleUpdateState = useCallback(
    (targetId: string, newState: DevicesDeviceState) => {
      const index = value.findIndex((target) => target.id === targetId);
      if (index < 0) return;
      const newTargets = [...value];
      const current = newTargets[index];
      if (!current) return;
      newTargets[index] = { ...current, state: pruneState(newState) };
      onChange(sortTargets(newTargets, devicesByHumanId));
    },
    [devicesByHumanId, value, onChange]
  );

  const handleUpdateDeactivationState = useCallback(
    (targetId: string, newState: DevicesDeviceState | undefined) => {
      const index = value.findIndex((target) => target.id === targetId);
      if (index < 0) return;
      const newTargets = [...value];
      const current = newTargets[index];
      if (!current) return;
      const prunedState = newState ? pruneState(newState) : undefined;
      const deactivationState =
        prunedState && Object.keys(prunedState).length > 0 ? prunedState : undefined;
      newTargets[index] = { ...current, deactivationState };
      onChange(sortTargets(newTargets, devicesByHumanId));
    },
    [devicesByHumanId, value, onChange]
  );

  // Check for validation errors on individual targets
  const getTargetError = useCallback((target: ScenesSceneDeviceRequest | null): string | null => {
    if (!target) return null;
    if (!hasIncludedProperties(target.state)) {
      return 'No properties selected';
    }
    return null;
  }, []);

  return (
    <div className="space-y-3">
      {/* Error message */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Two-pane layout - stacks on mobile, side-by-side on lg+ */}
      <div className="grid gap-4 lg:grid-cols-5">
        {/* Left pane: Target list */}
        <div className="order-1 lg:col-span-2 lg:h-full min-h-0">
          <div className="flex h-full min-h-0 flex-col rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-200 px-3 py-2 dark:border-white/[0.06]">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
                  Devices
                </span>
                <Badge variant="default" size="sm">
                  {value.length}
                </Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowAddDeviceModal(true)}
                disabled={selectableDevices.length === 0}
                leftIcon={<Plus className="h-3.5 w-3.5" />}
              >
                Add
              </Button>
            </div>

            {/* Target list */}
            {value.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center p-6 text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-zinc-800">
                  <Plus className="h-5 w-5 text-gray-400 dark:text-zinc-500" />
                </div>
                <p className="text-sm text-gray-500 dark:text-zinc-500">No devices added yet</p>
                <Button
                  variant="primary"
                  size="sm"
                  className="mt-3"
                  onClick={() => setShowAddDeviceModal(true)}
                  disabled={selectableDevices.length === 0}
                >
                  Add Device
                </Button>
              </div>
            ) : (
              <div className="flex-1 min-h-0 divide-y divide-gray-100 overflow-y-auto dark:divide-white/[0.04]">
                {sortedTargets.map((target) => {
                  const device = getDeviceInfo(target.id);
                  if (!device) return null;

                  const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;
                  const isSelected = selectedTargetId === target.id;
                  const targetError = getTargetError(target);
                  const summary = generateStateSummary(target.state, device);

                  return (
                    <div
                      key={target.id}
                      onClick={() => setSelectedTargetId(target.id ?? null)}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 px-3 py-2.5 transition-colors',
                        isSelected
                          ? 'bg-blue-50 dark:bg-blue-500/10'
                          : 'hover:bg-gray-50 dark:hover:bg-white/[0.02]',
                        targetError && 'ring-1 ring-inset ring-red-500/20'
                      )}
                    >
                      {/* Device icon */}
                      <div
                        className={cn(
                          'flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg',
                          isSelected
                            ? 'bg-blue-100 text-blue-600 dark:bg-blue-500/20 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-500'
                        )}
                      >
                        <Icon className="h-4 w-4" />
                      </div>

                      {/* Device info */}
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                            {device.name ?? device.humanId ?? device.id}
                          </span>
                          {target.deactivationState &&
                            Object.keys(target.deactivationState).length > 0 && (
                              <LogOut className="h-3.5 w-3.5 flex-shrink-0 text-gray-400 dark:text-zinc-500" />
                            )}
                          {targetError && (
                            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
                          )}
                        </div>
                        <div className="truncate text-xs text-gray-500 dark:text-zinc-500">
                          {summary}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex flex-shrink-0 items-center gap-0.5">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (target.id) {
                              handleRemoveDevice(target.id);
                            }
                          }}
                          className="rounded p-1 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10"
                          aria-label="Remove device"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right pane: Property editor */}
        <div className="order-2 lg:col-span-3 lg:min-h-[320px]">
          {selectedTarget && selectedDevice ? (
            <PropertyEditor
              device={selectedDevice}
              state={selectedTarget.state}
              deactivationState={selectedTarget.deactivationState}
              onChangeState={(newState) =>
                selectedTarget.id && handleUpdateState(selectedTarget.id, newState)
              }
              onChangeDeactivationState={(newState) =>
                selectedTarget.id && handleUpdateDeactivationState(selectedTarget.id, newState)
              }
              error={getTargetError(selectedTarget) ?? undefined}
            />
          ) : (
            <div className="flex h-full min-h-[200px] items-center justify-center rounded-xl border border-dashed border-gray-300 bg-gray-50/50 dark:border-white/[0.08] dark:bg-zinc-900/50">
              <div className="text-center">
                <p className="text-sm text-gray-500 dark:text-zinc-500">
                  {value.length === 0
                    ? 'Add a device to configure its target state'
                    : 'Select a device to edit its properties'}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add device modal */}
      <Modal
        isOpen={showAddDeviceModal}
        onClose={() => {
          setShowAddDeviceModal(false);
          setSearchQuery('');
        }}
        title="Add Device"
        subtitle="Select a device to add to this scene"
        size="md"
      >
        {/* Search input */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search devices..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-white/[0.08] dark:bg-zinc-900 dark:text-white dark:placeholder-zinc-500"
          />
        </div>

        {/* Device list */}
        <div className="max-h-80 overflow-y-auto rounded-lg border border-gray-200 dark:border-white/[0.06]">
          {devicesLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-5 w-5 animate-spin rounded-full border-2 border-gray-300 border-t-blue-500" />
            </div>
          ) : sortedFilteredDevices.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-gray-500 dark:text-zinc-500">
                {selectableDevices.length === 0
                  ? 'All devices have been added'
                  : 'No devices match your search'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-white/[0.04]">
              {sortedFilteredDevices.map((device) => {
                const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;
                const isOn =
                  device.state?.light?.on ||
                  device.state?.switch?.on ||
                  device.state?.tv?.on ||
                  device.state?.speaker?.on ||
                  device.state?.fan?.on;

                return (
                  <button
                    type="button"
                    key={device.id}
                    onClick={() => handleAddDevice(device.id!)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-gray-50 dark:hover:bg-white/[0.02]"
                  >
                    <div
                      className={cn(
                        'flex h-9 w-9 items-center justify-center rounded-lg',
                        isOn
                          ? 'bg-amber-50 text-amber-500 dark:bg-amber-500/10 dark:text-amber-400'
                          : 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-500'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {device.name ?? device.humanId ?? device.id}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-zinc-500">
                        {device.type} · {device.provider}
                        {isOn !== undefined && (
                          <span className="ml-2 text-gray-400 dark:text-zinc-600">
                            ({isOn ? 'On' : 'Off'})
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}

// Property editor component
type TabType = 'activation' | 'deactivation';

interface PropertyEditorProps {
  device: DevicesDevice;
  state: DevicesDeviceState;
  deactivationState?: DevicesDeviceState;
  onChangeState: (state: DevicesDeviceState) => void;
  onChangeDeactivationState: (state: DevicesDeviceState | undefined) => void;
  error?: string;
}

function PropertyEditor({
  device,
  state,
  deactivationState,
  onChangeState,
  onChangeDeactivationState,
  error,
}: PropertyEditorProps) {
  const [activeTab, setActiveTab] = useState<TabType>('activation');
  const Icon = DEVICE_ICONS[device.type ?? 'switch'] ?? Power;
  const propertyDefs = getPropertyDefsForDevice(device);

  // Get current state based on active tab - memoized to avoid useCallback issues
  const currentState = useMemo(
    () => (activeTab === 'activation' ? state : (deactivationState ?? {})),
    [activeTab, state, deactivationState]
  );
  const currentOnChange = useMemo(
    () =>
      activeTab === 'activation'
        ? onChangeState
        : (newState: DevicesDeviceState) => onChangeDeactivationState(newState),
    [activeTab, onChangeState, onChangeDeactivationState]
  );
  const includedPaths = getIncludedPropertyPaths(currentState);

  // Check if deactivation has any properties configured
  const hasDeactivationState = deactivationState && Object.keys(deactivationState).length > 0;

  // Separate common and advanced properties
  const commonProps = propertyDefs.filter((p) => p.category === 'common');
  const advancedProps = propertyDefs.filter((p) => p.category === 'advanced');

  const handleAddProperty = useCallback(
    (prop: PropertyDef) => {
      const defaultValue = prop.getDefaultValue(device);
      currentOnChange(setPropertyValue(currentState, prop.path, defaultValue));
    },
    [device, currentState, currentOnChange]
  );

  const handleRemoveProperty = useCallback(
    (path: string) => {
      currentOnChange(removeProperty(currentState, path));
    },
    [currentState, currentOnChange]
  );

  const handleUseCurrentValues = useCallback(() => {
    // Only update values for already-included property paths, don't add new properties
    const paths = getIncludedPropertyPaths(currentState);

    // If there are no included properties, do nothing
    if (paths.length === 0) {
      return;
    }

    // Update each included property with its current value from the device
    let newState = { ...currentState };
    for (const path of paths) {
      const currentValue = getPropertyValue(device.state ?? {}, path);
      if (currentValue !== undefined) {
        newState = setPropertyValue(newState, path, currentValue);
      }
      // If the device doesn't have a current value, leave the existing value untouched
    }

    currentOnChange(newState);
  }, [device, currentState, currentOnChange]);

  const handleClearAll = useCallback(() => {
    if (activeTab === 'deactivation') {
      onChangeDeactivationState(undefined);
    } else {
      onChangeState({});
    }
  }, [activeTab, onChangeState, onChangeDeactivationState]);

  // Available properties (not yet included)
  const availableCommon = commonProps.filter((p) => !isPropertyIncluded(currentState, p.path));
  const availableAdvanced = advancedProps.filter((p) => !isPropertyIncluded(currentState, p.path));
  const hasAvailableProps = availableCommon.length > 0 || availableAdvanced.length > 0;

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-white/[0.06] dark:bg-[#18181b]">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 py-3 dark:border-white/[0.06]">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gray-100 text-gray-600 dark:bg-zinc-800 dark:text-zinc-400">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <div className="font-medium text-gray-900 dark:text-white">
                {device.name ?? device.humanId ?? device.id}
              </div>
              <div className="text-sm text-gray-500 dark:text-zinc-500">
                {device.type} · {device.provider}
              </div>
            </div>
          </div>
          <Dropdown
            trigger={<IconButton icon={<MoreVertical className="h-4 w-4" />} label="Actions" />}
            align="right"
            onSelect={(value) => {
              if (value === 'use-current') {
                handleUseCurrentValues();
              } else if (value === 'clear') {
                handleClearAll();
              }
            }}
            items={[
              {
                value: 'use-current',
                label: 'Use current',
              },
              {
                value: 'clear',
                label: 'Clear all',
                variant: 'danger',
              },
            ]}
          />
        </div>
      </div>

      {/* Tab bar */}
      <div className="flex border-b border-gray-200 dark:border-white/[0.06]">
        <button
          type="button"
          onClick={() => setActiveTab('activation')}
          className={cn(
            'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
            activeTab === 'activation'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300'
          )}
        >
          On Activation
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('deactivation')}
          className={cn(
            'flex-1 px-4 py-2.5 text-sm font-medium transition-colors',
            activeTab === 'deactivation'
              ? 'border-b-2 border-blue-500 text-blue-600 dark:text-blue-400'
              : 'text-gray-500 hover:text-gray-700 dark:text-zinc-500 dark:hover:text-zinc-300',
            hasDeactivationState && activeTab !== 'deactivation' && 'relative'
          )}
        >
          On Deactivation
          {hasDeactivationState && activeTab !== 'deactivation' && (
            <span className="ml-1.5 inline-flex h-2 w-2 rounded-full bg-blue-500" />
          )}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-600 dark:bg-red-500/10 dark:text-red-400">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Properties */}
      <div className="p-4">
        {/* Included property chips */}
        {includedPaths.length > 0 && (
          <div className="mb-4">
            <div className="mb-2 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
              Included Properties
            </div>
            <div className="flex flex-wrap gap-2">
              {includedPaths.map((path) => {
                const prop = propertyDefs.find((p) => p.path === path);
                if (!prop) return null;
                return (
                  <span
                    key={path}
                    className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 py-1 pl-3 pr-1.5 text-sm text-blue-700 dark:bg-blue-500/10 dark:text-blue-400"
                  >
                    {prop.label}
                    <button
                      type="button"
                      onClick={() => handleRemoveProperty(path)}
                      className="rounded-full p-0.5 transition-colors hover:bg-blue-100 dark:hover:bg-blue-500/20"
                      aria-label={`Remove ${prop.label}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Add property button */}
        {hasAvailableProps && (
          <Popover
            align="left"
            trigger={
              <Button variant="outline" size="sm" leftIcon={<Plus className="h-3.5 w-3.5" />}>
                Add property
              </Button>
            }
            content={
              <div className="max-h-64 w-56 overflow-y-auto p-1">
                {availableCommon.length > 0 && (
                  <>
                    <div className="px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-zinc-500">
                      Common
                    </div>
                    {availableCommon.map((prop) => (
                      <button
                        type="button"
                        key={prop.path}
                        onClick={() => handleAddProperty(prop)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-white/[0.04]"
                      >
                        {prop.label}
                      </button>
                    ))}
                  </>
                )}
                {availableAdvanced.length > 0 && (
                  <>
                    <div className="mt-2 border-t border-gray-100 px-2 py-1.5 text-xs font-medium uppercase tracking-wider text-gray-500 dark:border-white/[0.04] dark:text-zinc-500">
                      Advanced
                    </div>
                    {availableAdvanced.map((prop) => (
                      <button
                        type="button"
                        key={prop.path}
                        onClick={() => handleAddProperty(prop)}
                        className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm text-gray-700 transition-colors hover:bg-gray-100 dark:text-zinc-300 dark:hover:bg-white/[0.04]"
                      >
                        {prop.label}
                      </button>
                    ))}
                  </>
                )}
              </div>
            }
          />
        )}

        {/* No properties message */}
        {includedPaths.length === 0 && !hasAvailableProps && (
          <p className="text-sm text-gray-500 dark:text-zinc-500">
            No configurable properties for this device type.
          </p>
        )}

        {/* Deactivation tab empty state message */}
        {activeTab === 'deactivation' && includedPaths.length === 0 && hasAvailableProps && (
          <div className="mt-4 rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/50">
            <p className="text-sm text-gray-600 dark:text-zinc-400">
              No deactivation state configured. The device will be left as-is when the scene
              deactivates.
            </p>
            <p className="mt-2 text-xs text-gray-500 dark:text-zinc-500">
              Add properties to set specific values when this scene ends.
            </p>
          </div>
        )}

        {/* Property value controls */}
        {includedPaths.length > 0 && (
          <div className="mt-4 space-y-4 border-t border-gray-100 pt-4 dark:border-white/[0.04]">
            {includedPaths.map((path) => {
              const prop = propertyDefs.find((p) => p.path === path);
              if (!prop) return null;
              return (
                <PropertyValueControl
                  key={path}
                  property={prop}
                  device={device}
                  value={getPropertyValue(currentState, path)}
                  onChange={(value) => currentOnChange(setPropertyValue(currentState, path, value))}
                  onRemove={() => handleRemoveProperty(path)}
                />
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// Property value control
interface PropertyValueControlProps {
  property: PropertyDef;
  device: DevicesDevice;
  value: unknown;
  onChange: (value: unknown) => void;
  onRemove: () => void;
}

function PropertyValueControl({
  property,
  device,
  value,
  onChange,
  onRemove,
}: PropertyValueControlProps) {
  // Handle color property specially
  if (property.path === 'light.color') {
    const colorValue = value as { red?: number; green?: number; blue?: number } | undefined;
    return (
      <div className="rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/50">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            {property.label}
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-zinc-700 dark:hover:text-white"
            aria-label={`Remove ${property.label}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {COLOR_PRESETS.map((color) => {
            const isSelected =
              colorValue?.red === color.red &&
              colorValue?.green === color.green &&
              colorValue?.blue === color.blue;
            return (
              <button
                type="button"
                key={color.name}
                onClick={() => onChange({ red: color.red, green: color.green, blue: color.blue })}
                className={cn(
                  'h-8 w-8 rounded-full border-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500/50',
                  isSelected
                    ? 'border-blue-500 ring-2 ring-blue-500/30'
                    : 'border-gray-200 hover:border-gray-300 dark:border-zinc-700 dark:hover:border-zinc-600'
                )}
                style={{
                  backgroundColor: `rgb(${color.red}, ${color.green}, ${color.blue})`,
                }}
                aria-label={color.name}
                aria-pressed={isSelected}
              />
            );
          })}
        </div>
      </div>
    );
  }

  // Toggle control
  if (property.controlType === 'toggle') {
    return (
      <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 dark:bg-zinc-800/50">
        <Toggle
          label={property.label}
          checked={Boolean(value)}
          onChange={(checked) => onChange(checked)}
          layout="switch-right"
        />
        <button
          type="button"
          onClick={onRemove}
          className="ml-2 rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-zinc-700 dark:hover:text-white"
          aria-label={`Remove ${property.label}`}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    );
  }

  // Slider control
  if (property.controlType === 'slider' && property.sliderConfig) {
    return (
      <div className="rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/50">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            {property.label}
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-zinc-700 dark:hover:text-white"
            aria-label={`Remove ${property.label}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <Slider
          label={property.label}
          hideLabel
          min={property.sliderConfig.min}
          max={property.sliderConfig.max}
          value={Number(value) || 0}
          onChange={(v) => onChange(v)}
          formatValue={property.sliderConfig.formatValue}
          hint={property.sliderConfig.hint}
        />
      </div>
    );
  }

  // Select control
  if (property.controlType === 'select' && property.getSelectOptions) {
    const options = property.getSelectOptions(device);
    return (
      <div className="rounded-lg bg-gray-50 p-3 dark:bg-zinc-800/50">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700 dark:text-zinc-300">
            {property.label}
          </span>
          <button
            type="button"
            onClick={onRemove}
            className="rounded p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 dark:hover:bg-zinc-700 dark:hover:text-white"
            aria-label={`Remove ${property.label}`}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
        <Select
          label={property.label}
          hideLabel
          value={String(value ?? '')}
          onChange={(v) => onChange(v)}
          options={options}
          placeholder="Select..."
          fullWidth
        />
      </div>
    );
  }

  return null;
}
